import React, { useState, useEffect, useContext } from 'react';
import { styled } from 'styled-components';
import { AnimatePresence, motion } from 'motion/react';
import { Markup } from 'interweave';
import { navigate } from 'gatsby';
import { useTranslations } from 'use-intl';
import { getTimeRemaining, useWindowSize, usePresence } from 'hooks';
import { Calendar, ExpandIcon } from 'assets/svgs';
import { FirebaseContext } from 'context';
import { Button } from 'components';
import ModeratorOverview from './ModeratorOverview';
import ContributorOverview from './ContributorOverview';
import Moderators from './Moderators';
import RelatedMaterials from './RelatedMaterials';

function SessionOverview({ session, totalQuestions }) {
  const { id: sessionId, endDate, textDate } = session;
  const t = useTranslations('Session');
  const { width } = useWindowSize();
  const [timeRemaining, setTimeRemaining] = useState(
    getTimeRemaining({ endDate, daysExtended: 0 })
  );
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);

  const [moderators, setModerators] = useState([]);
  const [contributors, setContributors] = useState([]);

  const { firebase, user } = useContext(FirebaseContext);

  // Initialize presence tracking
  usePresence({ firebase, user, sessionId });

  const variants = {
    container: {
      hidden: { opacity: 0, padding: '1.5rem 1.3125rem' },
      visible: {
        opacity: 1,
        padding: '1.95rem 1.3125rem 1.5rem',
        transiton: {
          duration: 2,
          opacity: {
            delay: 1.5,
            duration: 0.5
          }
        }
      }
    },
    item: {
      hidden: { opacity: 0, height: 0, marginBottom: 0 },
      visible: {
        opacity: 1,
        height: '100%',
        paddingBottom: width < 1200 ? '1.625em' : '1.5em',
        transition: {
          delay: 0.5,
          ease: [0, 0.71, 0.2, 1.01],
          duration: 0.5
        }
      }
    }
  };

  useEffect(() => {
    let unsubscribeToAllSessionContributors;
    let unsubscribeToAllSessionComments;
    if (firebase) {
      unsubscribeToAllSessionContributors =
        firebase.moderator.subscribeToAllSessionContributors({
          sessionId,
          snapshot: (snapshot) => {
            const _moderators = [];
            const _contributors = [];
            if (!snapshot.empty) {
              snapshot.forEach((doc) => {
                const { isModerator } = doc.data();
                if (isModerator.includes(sessionId)) {
                  _moderators.push({
                    id: doc.id,
                    ...doc.data()
                  });
                } else {
                  _contributors.push({
                    id: doc.id,
                    ...doc.data()
                  });
                }
              });
            }
            setModerators(_moderators);
            setContributors(_contributors);
          }
        });
      unsubscribeToAllSessionComments =
        firebase.questions.subscribeToAllSessionComments({
          sessionId,
          snapshot: (snapshot) => {
            const _comments = [];
            if (!snapshot.empty) {
              snapshot.forEach((doc) => {
                _comments.push({
                  id: doc.id,
                  ...doc.data()
                });
              });
            }
            setComments(_comments);
          }
        });
    }
    return () => {
      if (unsubscribeToAllSessionContributors) {
        unsubscribeToAllSessionContributors();
      }
      if (unsubscribeToAllSessionComments) {
        unsubscribeToAllSessionComments();
      }
    };
  }, [firebase]);

  // Subscribe to presence data
  useEffect(() => {
    let unsubscribeToPresence;
    if (firebase && sessionId) {
      unsubscribeToPresence = firebase.presence.subscribeToSessionPresence({
        sessionId,
        callback: (_onlineUsers) => {
          setOnlineUsers(_onlineUsers);
        }
      });
    }
    return () => {
      if (unsubscribeToPresence) {
        unsubscribeToPresence();
      }
    };
  }, [firebase, sessionId]);

  useEffect(() => {
    if (width >= 1200) {
      setExpanded(true);
    } else {
      setExpanded(false);
    }
  }, [width]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(getTimeRemaining({ endDate, daysExtended: 0 }));
    }, 1000);

    return () => clearInterval(timer);
  }, [endDate]);

  return (
    <AnimatePresence>
      <OverviewContainer
        variants={variants.container}
        initial="hidden"
        animate="visible"
        exit="hidden"
        layout>
        <AnimatePresence>
          {(user?.isModerator?.includes(sessionId) || expanded) && (
            <TimeRemaining
              key="timeRemaining"
              variants={variants.item}
              initial="hidden"
              animate="visible"
              exit="hidden">
              {t('timeRemaining', timeRemaining)}
              {/* 00d 00h:00m */}
              <span>{t('timeLeftToParticipate')}</span>
            </TimeRemaining>
          )}
          {expanded && (
            <TextDate
              key="textDate"
              variants={variants.item}
              initial="hidden"
              animate="visible"
              exit="hidden">
              <Calendar width="30px" />
              <Markup content={textDate.split(',')[1]} />
            </TextDate>
          )}
          {expanded && user && user.isModerator?.includes(sessionId) && (
            <ModeratorOverview
              sessionId={sessionId}
              contributors={contributors}
              comments={comments}
              variants={variants.item}
              totalQuestions={totalQuestions}
              onlineUsers={onlineUsers}
            />
          )}
          {user && !user.isModerator?.includes(sessionId) && (
            <ContributorOverview
              comments={comments}
              contributorId={user.uid}
              expanded={expanded}
              variants={variants.item}
              totalQuestions={totalQuestions}
            />
          )}
          {expanded && (
            <Moderators
              moderators={moderators}
              variants={variants.item}
              onlineUsers={onlineUsers}
            />
          )}
          {expanded && (
            <RelatedMaterials
              sessionId={sessionId}
              isModerator={user && user.isModerator?.includes(sessionId)}
              variants={variants.item}
            />
          )}
          {expanded &&
            user?.isModerator?.includes(sessionId) &&
            user.email.includes('agencyx.ie') && (
              <AnalyticsButtonWrapper>
                <Button
                  onClick={() => navigate(`/analytics?sessionId=${sessionId}`)}
                  backgroundColor="var(--blue)"
                  textColor="#fff"
                  style={{
                    fontSize: '0.875rem',
                    padding: '0.5em 1.5em'
                  }}>
                  {t('viewAnalytics')}
                </Button>
              </AnalyticsButtonWrapper>
            )}
        </AnimatePresence>
        {!expanded ? (
          <ToggleOverview onClick={() => setExpanded(true)}>
            <ExpandIcon />
            {t('expandOverview')}
          </ToggleOverview>
        ) : (
          expanded &&
          width < 1200 && (
            <ToggleOverview
              onClick={() => setExpanded(false)}
              expanded={expanded}>
              <ExpandIcon />
              {t('hideOverview')}
            </ToggleOverview>
          )
        )}
      </OverviewContainer>
    </AnimatePresence>
  );
}

const OverviewContainer = styled(motion.div)`
  align-items: center;
  background: #fff;
  border-radius: var(--border-radius-md);
  display: flex;
  flex-direction: column;
  grid-column-gap: 1.25rem;
  height: min-content;
  justify-content: flex-start;
  margin: 0 1.25rem;
  position: relative;
  top: -6rem;
  /* z-index: -1; */

  h4 {
    color: var(--blue);
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    text-transform: uppercase;
  }

  > * {
    grid-column: 1 / -1;
  }

  @media (min-width: 1200px) {
    margin: 0;
    grid-column: 9/12;
    padding: 1.95rem 1.3125rem;
  }
`;

const TimeRemaining = styled(motion.h3)`
  align-items: center;
  color: var(--blue);
  display: flex;
  flex-direction: column;
  font-family: 'Poppins', sans-serif;
  font-size: 2rem;
  font-weight: 600;
  gap: 0.5rem;
  justify-content: center;
  line-height: 110%;
  margin: 0;
  text-align: center;
  width: 100%;
  span {
    color: var(--brown);
    font-size: 1.5rem;
    font-weight: 400;
  }
`;

const TextDate = styled(motion.div)`
  align-items: center;
  color: var(--blue);
  display: grid;
  font-size: 1rem;
  gap: 1rem;
  grid-template-columns: 32px 1fr;
  justify-content: center;
  width: 100%;
  p {
    margin: 0;
  }
`;

const AnalyticsButtonWrapper = styled.div`
  border-top: 1px solid var(--light-grey);
  display: flex;
  justify-content: center;
  padding-top: 1.5rem;
  width: 100%;

  @media (max-width: 1199px) {
    padding-bottom: 1.25rem;
  }
`;

const ToggleOverview = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: var(--blue);
  cursor: pointer;
  display: flex;
  font-size: 0.75rem;
  font-weight: 400;
  gap: 0.5rem;
  justify-content: center;
  padding: 0;
  text-align: center;
  svg {
    height: auto;
    transform: ${({ expanded }) =>
      expanded
        ? 'rotate(180deg) translateY(-0.1rem)'
        : 'rotate(0deg) translateY(0)'};
    transition: transform 0.3s ease-in-out;
    width: 0.5rem;
  }
`;

export default SessionOverview;
